/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Toolbar, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { usePostRequest } from "../../../../api/helper";
import { theme } from "../../../../constants/theme";
import {
    editDatasetLocalState,
    EEditDatasetLocalStateActionTypes,
    selectCoverageRequirementsClipboard,
    setCoverageRequirementsClipboard
} from "../../../../redux/ducks/CoverageRequirements/slice";
import { selectGlobalDialogState } from "../../../../redux/ducks/GlobalDialog/slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import CloseGlobalDialogButton from "../../../components/Global/Dialog/CloseGlobalDialogButton";
import Loader from "../../../components/Loader/Loader";
import useGetHubName from "../../../components/Socket/useGetHubName";
import useSocket from "../../../components/Socket/useSocket";
import {
    CoverageRequirementsHorizontalTabs
} from "../../../components/Tabs/CoverageRequirementsHorizontalTabs/CoverageRequirementsHorizontalTabs";
import { editStudyEndPoint, coverageRequirementsHeaderTitle } from "../constants";
import { COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_ONE_INDEX_URL_PARAM, coverageRequirementsFetchData } from "./constants";
import CoverageRequirements from "./Tabs/CoverageRequirementsTab/CoverageRequirements";
import DashboardTab from "./Tabs/DashboardTab";
import Description from "./Tabs/Description";
import ExecutionStatus from "./Tabs/ExecutionStatusTab/ExecutionStatus";
import {selectCurrentUser} from "../../../../redux/ducks/AccessControl/slice";
import {useGetIsUserAdmin} from "../../../../redux/ducks/AccessControl/accessControlHelpers";


export const useDynamicThrottledUpdates2 = (dispatch: any, minInterval: number, maxInterval: number) => {
  const recentMessagesRef = useRef<any>({});
  const [updateInterval, setUpdateInterval] = useState(minInterval);
  const messageCountRef = useRef(0);

  const onMessage = useCallback((msg: any) => {
    if (msg?.type !== 'redlineRunProgress') return;
    const { datasetId } = msg;
    recentMessagesRef.current[datasetId] = msg;
    messageCountRef.current += 1;
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      // Process the stored messages
      if(Object.keys(recentMessagesRef.current).length > 0) {
        const datasetIdsToData: any = {}
        Object.entries(recentMessagesRef.current).forEach(([datasetId, data]: any) => {
          const {progress, totalSteps, currentStep} = data;
          const fraction = totalSteps !== 0 ? `${currentStep} / ${totalSteps}` : ' ';
          datasetIdsToData[datasetId] = {progress, totalSteps, currentStep, fraction};
        });
        dispatch(editDatasetLocalState({type: EEditDatasetLocalStateActionTypes.MULTIPLE, data: datasetIdsToData}));
        recentMessagesRef.current = {};
      }

      // Adjust the update interval based on the number of messages received
      if (messageCountRef.current >= 10) {
        setUpdateInterval(prevInterval => Math.min(maxInterval, prevInterval + 1000));
      } else if (messageCountRef.current < 10 && updateInterval > minInterval) {
        setUpdateInterval(prevInterval => Math.max(minInterval, prevInterval - 1000));
      }
      messageCountRef.current = 0; // Reset message count after adjustment
    }, updateInterval);

    return () => clearInterval(intervalId);
  }, [updateInterval, dispatch, minInterval, maxInterval]);

  return onMessage;
};

const CoverageRequirementsStudyDetails = (props: any) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = useMemo(() => {
    return Object.fromEntries(searchParams)
  }, [searchParams])
  const isDialogOpen = useAppSelector((state) => selectGlobalDialogState(state).isOpen)
  const [studyName, setStudyName] = useState<string>("");
  const [isPlanOfRecord, setIsPlanOfRecord] = useState<boolean>();
  const [isStudyArchive, setIsStudyArchive] = useState<boolean>();
  const [owner, setIsStudyOwner] = useState<string>()
  const gmid = useAppSelector(state => selectCurrentUser(state).id);

  const [datasetChange, setDatasetChange] = useState<number>(1)
  const [isReadOnlyStudy, setIsReadOnlyStudy]= useState<boolean| undefined>(false)
  const isUserAdmin = useGetIsUserAdmin()
  useEffect(() =>{
    // Determine if the study is read-only based on the isStudyArchive setting
    setIsReadOnlyStudy(isStudyArchive)
  }, [isStudyArchive])
  const params: any = useAppSelector(state => {
    const dialogState = selectGlobalDialogState(state)
    if (isDialogOpen) {
      return dialogState.component?.params ?? urlParams
    } else return urlParams
  })
  const studyId = params["studyId"]
  const [isLoading, setIsLoading] = useState(false);
  const [tabs, setTabs] = useState<any[]>([]);
  const fetchDataHandle = usePostRequest(coverageRequirementsFetchData)
  const editStudyFetchHandle = usePostRequest(editStudyEndPoint)
  
  function isPlanOfRecordTrueOrFalse(){
    let isPlanOfRecordResult = ""
    if (isPlanOfRecord === false){
      return isPlanOfRecordResult = "— Not Plan of Record"
    }
    else
    {
      return isPlanOfRecordResult = "— Plan of Record"
    }
  }

  useEffect(() => {
    if (studyId) {
      fetchData()
    }
  }, [studyId, isReadOnlyStudy])

   /**
   * Socket to receive redline data progress
   */
  const onMessage = useDynamicThrottledUpdates2(dispatch, 3000, 5000);
  const hubName = useGetHubName(`redlineRunProgress_${studyId}`)
  useSocket(hubName, onMessage)

  const fetchData = () => {
    fetchDataHandle({"studyId": studyId})
      .then((data) => {
        populateTabs(data)
        setStudyName(data["study_data"]['name'])
        setIsPlanOfRecord(data["study_data"]['isPlanOfRecord'])
        setIsStudyArchive(data["study_data"]['isStudyArchive'])
        setIsStudyOwner(data["study_data"]['owner'])
      })
      .catch((e) => {
        toast.error("We encountered an error while loading the studies data")
      })
  }
  const editStudy = (editData: any) => {
    editStudyFetchHandle(editData)
      .then((data) => {
        populateTabs(data)
        setStudyName(data["study_data"]['name'])
        setIsPlanOfRecord(data["study_data"]['isPlanOfRecord'])
        setIsStudyArchive(data["study_data"]["isStudyArchive"])
      })
      .catch((e) => {
        toast.error("Unable to add study, please try again")
      })
  }
  const sortFu = (rowA: any, rowB: any) => {
    if (rowA === null) return 1;
    if (rowB === null) return -1;
      if (rowA !== rowB) {
        const aIsNumber = !isNaN(rowA);
        const bIsNumber = !isNaN(rowB);
        if (aIsNumber && bIsNumber) {
          return  parseInt(rowA) < parseInt(rowB)  ? 1 : -1;
        } else {
          return String(rowA).localeCompare(String(rowB));
        }
      }
    return 0;    
  }
  const populateTabs = (data: any) => {
    const localTabs: any[] = []
    const descriptionContent = <Description
      studyData={data["study_data"]}
      editStudy={editStudy}
      isPlanOfRecord={isPlanOfRecord}
      isReadOnlyStudy={isReadOnlyStudy}
    />
    const descriptionTab = {'name': "Description", 'content': descriptionContent}
    const coverageRequirementsContent = <CoverageRequirements
      studyId={studyId}
      studyName={studyName}
      isPlanOfRecord={isPlanOfRecord}
      isReadOnlyStudy={isReadOnlyStudy}
      setDatasetChange={setDatasetChange}
      datasetChange={datasetChange}
      fetchStudy={fetchData}
    />
    const coverageRequirementsTab = {'name': "Coverage Analysis", 'content': coverageRequirementsContent}
    const executionStatusTab = {'name': "Execution Status", 'content': <ExecutionStatus key={"executionStatusTab"} studyId={studyId} sortFu={sortFu}/>}
    const dashboardTab = {'name': "Dashboard", 'content': <DashboardTab studyId={studyId} sortFu={sortFu}/>}

    localTabs.push(coverageRequirementsTab);
    localTabs.push(executionStatusTab);
    localTabs.push(dashboardTab);
    localTabs.push(descriptionTab);
    
    setTabs(localTabs);
  }

  const coverageRequirementsClipboard = useAppSelector(state => selectCoverageRequirementsClipboard(state))

  useEffect(() => {

    function handleKeyDown(e: any) {

      if(e.keyCode === 27 && coverageRequirementsClipboard == null){
        toast.success("Nothing to clear. Your clipboard is Empty!")
         return
      }
      if(e.keyCode === 27 && coverageRequirementsClipboard !== null){
        dispatch(setCoverageRequirementsClipboard(null))
        toast.success("Successfully cleared the clipboard!")
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [coverageRequirementsClipboard]);

  useEffect(() => {
    if(studyName)
      document.title = coverageRequirementsHeaderTitle + ' - ' + studyName;
    return () => {
      document.title = coverageRequirementsHeaderTitle + ' - Landing';
    };
  }, [studyName]);
  
  return (
    <div style={{height: "100%", overflow: "hidden"}}>
      {studyName != "" &&
        <Toolbar
          variant="dense"
          style={{
            alignItems: "center",
            justifyContent: "space-between",
            background: theme.layout.sidebar.backgroundColor,
            height: "5%"
          }}
        >
          <Typography style={{color: "white"}}>
            {studyName} {isPlanOfRecordTrueOrFalse()} {isStudyArchive && "(Archived)"}
          </Typography>
          <CloseGlobalDialogButton/>
        </Toolbar>

      }
      <Loader isLoading={isLoading}>
        <ArgosFlexbox style={{height: "95%"}}>
          <div style={{flex: 1, overflow: 'auto'}}>
            <CoverageRequirementsHorizontalTabs 
              uid={COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_ONE_INDEX_URL_PARAM}
              initContent={null} initHtabIndex={0} horizontalTabs={tabs}
              align={'left'}
            />
          </div>
        </ArgosFlexbox>

      </Loader>
    </div>
  )
}

export default CoverageRequirementsStudyDetails;